import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Loader2, Camera, AlertCircle, Smartphone, Shield } from 'lucide-react';
import OnboardingInterests from './OnboardingInterests';
import OnboardingFollowSuggestions from './OnboardingFollowSuggestions';
import OnboardingHubSuggestions from './OnboardingHubSuggestions';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface OnboardingFlowProps {
  onComplete: () => void;
  needsProfile?: boolean; // true if profile_completed_at is null
}

type OnboardingStep = 'profile' | 'language' | 'interests' | 'follow' | 'hubs';

export default function OnboardingFlow({ onComplete, needsProfile = false }: OnboardingFlowProps) {
  const { t } = useTranslation();
  // Language is the first step after profile (or first overall when
  // profile is already complete). Without it, users.content_languages
  // stays null and the language filter from #154 defaults them to
  // English-only — Israeli viewers see no Hebrew.
  const STEPS: OnboardingStep[] = needsProfile
    ? ['profile', 'language', 'interests', 'follow', 'hubs']
    : ['language', 'interests', 'follow', 'hubs'];

  const [currentStep, setCurrentStep] = useState<OnboardingStep>(STEPS[0]);
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  const [followedUserIds, setFollowedUserIds] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const getAuthToken = useCallback(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('authToken');
    return null;
  }, []);

  const currentStepIndex = STEPS.indexOf(currentStep);

  const handleProfileComplete = () => {
    setCurrentStep('language');
  };

  const handleLanguageNext = async (codes: string[]) => {
    const token = getAuthToken();
    if (token) {
      try {
        await fetch(`${API_URL}/user/content-languages`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ content_languages: codes }),
        });
      } catch { /* Non-critical — user can edit in Settings later */ }
    }
    setCurrentStep('interests');
  };

  const handleInterestsNext = async (topicIds: string[]) => {
    setSelectedTopicIds(topicIds);
    const token = getAuthToken();
    if (token) {
      try {
        await fetch(`${API_URL}/onboarding/interests`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ topicIds }),
        });
      } catch { /* Non-critical */ }
    }
    setCurrentStep('follow');
  };

  const handleFollowNext = async (userIds: string[]) => {
    setFollowedUserIds(userIds);
    const token = getAuthToken();
    if (token && userIds.length > 0) {
      try {
        await fetch(`${API_URL}/follows/bulk`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ userIds }),
        });
      } catch { /* Non-critical */ }
    }
    setCurrentStep('hubs');
  };

  const handleHubsComplete = async (hubIds: string[]) => {
    setIsCompleting(true);
    const token = getAuthToken();
    if (token && hubIds.length > 0) {
      try {
        await fetch(`${API_URL}/collections/bulk-join`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ hubIds }),
        });
      } catch { /* Non-critical */ }
    }
    if (token) {
      try {
        await fetch(`${API_URL}/onboarding/complete`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
      } catch { /* Non-critical */ }
    }
    setIsCompleting(false);
    onComplete();
  };

  const handleSkip = async () => {
    // Profile step cannot be skipped if needed
    if (currentStep === 'profile') return;
    const token = getAuthToken();
    if (token) {
      try {
        await fetch(`${API_URL}/onboarding/complete`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
      } catch { /* Non-critical */ }
    }
    onComplete();
  };

  const stepLabels: Record<OnboardingStep, string> = {
    profile: t('onboarding.steps.profile'),
    language: t('onboarding.steps.language', 'Language'),
    interests: t('onboarding.steps.interests'),
    follow: t('onboarding.steps.follow'),
    hubs: t('onboarding.steps.hubs'),
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-100 bg-black/80 backdrop-blur-md transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-101 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`relative w-full max-w-2xl max-h-[90vh] bg-[#0a0a12] border border-white/10 rounded-2xl shadow-2xl pointer-events-auto transition-all duration-300 overflow-hidden flex flex-col ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          {/* Progress Header */}
          <div className="px-6 py-4 border-b border-white/6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-y-2">
              <h2 className="text-lg font-semibold text-white">
                {t('onboarding.welcomeTitle')}
              </h2>
              {currentStep !== 'profile' && (
                <button
                  onClick={handleSkip}
                  className="p-2 rounded-lg text-[#9a9aac] hover:text-white hover:bg-white/5 transition-all"
                >
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Progress Steps */}
            <div className="flex items-center gap-2">
              {STEPS.map((step, index) => {
                const isComplete = index < currentStepIndex;
                const isCurrent = step === currentStep;

                return (
                  <React.Fragment key={step}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                          isComplete
                            ? 'bg-emerald-500 text-white'
                            : isCurrent
                            ? 'bg-iris-600 text-[#0C0C12]'
                            : 'bg-white/6 text-[#9a9aac]'
                        }`}
                      >
                        {isComplete ? <Check size={14} /> : index + 1}
                      </div>
                      <span
                        className={`text-sm hidden sm:inline ${
                          isCurrent ? 'text-white font-medium' : 'text-[#9a9aac]'
                        }`}
                      >
                        {stepLabels[step]}
                      </span>
                    </div>
                    {index < STEPS.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 rounded-full transition-colors ${
                          index < currentStepIndex ? 'bg-emerald-500' : 'bg-white/6'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isCompleting ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 size={32} className="text-iris-500 animate-spin mb-4" />
                <p className="text-[#9a9aac]">{t('onboarding.settingUp')}</p>
              </div>
            ) : (
              <>
                {currentStep === 'profile' && (
                  <OnboardingProfile onNext={handleProfileComplete} />
                )}
                {currentStep === 'language' && (
                  <OnboardingLanguageStep onNext={handleLanguageNext} />
                )}
                {currentStep === 'interests' && (
                  <OnboardingInterests onNext={handleInterestsNext} />
                )}
                {currentStep === 'follow' && (
                  <OnboardingFollowSuggestions
                    topicIds={selectedTopicIds}
                    onNext={handleFollowNext}
                    onSkip={() => setCurrentStep('hubs')}
                  />
                )}
                {currentStep === 'hubs' && (
                  <OnboardingHubSuggestions
                    topicIds={selectedTopicIds}
                    onComplete={handleHubsComplete}
                    onSkip={() => handleHubsComplete([])}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Profile Setup Step ──────────────────────────────────────────────────────

function OnboardingProfile({ onNext }: { onNext: () => void }) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [birthday, setBirthday] = useState('');
  const [gender, setGender] = useState('');
  const [location, setLocation] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [usernameStatus, setUsernameStatus] = useState<{ checking: boolean; available?: boolean; error?: string }>({ checking: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Phone verification state
  const [phone, setPhone] = useState('');
  const [phoneStep, setPhoneStep] = useState<'idle' | 'sending' | 'code' | 'verified'>('idle');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [phoneCountdown, setPhoneCountdown] = useState(0);

  const getAuthToken = useCallback(() => {
    if (typeof window !== 'undefined') return localStorage.getItem('authToken');
    return null;
  }, []);

  // Auto-suggest username from display name (updates as user types)
  const [userEditedUsername, setUserEditedUsername] = useState(false);
  useEffect(() => {
    if (displayName && !userEditedUsername) {
      const suggested = displayName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 20);
      if (suggested.length >= 2) setUsername(suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayName]);

  // Check username availability
  useEffect(() => {
    if (!username || username.length < 3) {
      setUsernameStatus({ checking: false });
      return;
    }
    setUsernameStatus({ checking: true });
    const timeout = setTimeout(() => {
      const token = getAuthToken();
      fetch(`${API_URL}/user/username/check?username=${encodeURIComponent(username)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then(r => r.json())
        .then(data => setUsernameStatus({ checking: false, available: data.available, error: data.reason }))
        .catch(() => setUsernameStatus({ checking: false }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [username, getAuthToken]);

  // Phone countdown timer
  useEffect(() => {
    if (phoneCountdown <= 0) return;
    const timer = setInterval(() => {
      setPhoneCountdown(prev => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [phoneCountdown]);

  const handleSendPhoneCode = async () => {
    const sanitized = phone.replace(/[\s\-()]/g, '');
    if (!/^\+\d{7,15}$/.test(sanitized)) {
      setPhoneError(t('onboarding.profile.phoneInvalid', 'Enter a valid phone with country code (e.g. +1234567890)'));
      return;
    }
    setPhoneStep('sending');
    setPhoneError('');
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/phone/send-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: sanitized }),
      });
      const data = await res.json() as { success?: boolean; error?: string };
      if (res.ok && data.success) {
        setPhoneStep('code');
        setPhoneCode('');
        setPhoneCountdown(60);
      } else {
        setPhoneError(data.error || 'Failed to send code');
        setPhoneStep('idle');
      }
    } catch {
      setPhoneError('Failed to send verification code');
      setPhoneStep('idle');
    }
  };

  const handleVerifyPhoneCode = async () => {
    const code = phoneCode.replace(/\D/g, '');
    if (code.length !== 6) { setPhoneError('Code must be 6 digits'); return; }
    setPhoneVerifying(true);
    setPhoneError('');
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/phone/verify-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json() as { success?: boolean; error?: string };
      if (res.ok && data.success) {
        setPhoneStep('verified');
      } else {
        setPhoneError(data.error || 'Verification failed');
      }
    } catch {
      setPhoneError('Verification failed');
    } finally {
      setPhoneVerifying(false);
    }
  };

  // Handle avatar upload
  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError(t('onboarding.profile.avatarTooLarge'));
      return;
    }
    setAvatarFile(file);
    const url = URL.createObjectURL(file);
    setAvatarUrl(url);
  };

  // Age validation
  const getAge = (dateStr: string): number => {
    const bday = new Date(dateStr);
    const today = new Date();
    let age = today.getFullYear() - bday.getFullYear();
    const m = today.getMonth() - bday.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < bday.getDate())) age--;
    return age;
  };

  const handleSubmit = async () => {
    setError('');

    if (!displayName.trim()) { setError(t('onboarding.profile.nameRequired')); return; }
    if (!username || username.length < 3) { setError(t('onboarding.profile.usernameRequired')); return; }
    if (usernameStatus.available === false) { setError(t('onboarding.profile.usernameTaken')); return; }
    if (!birthday) { setError(t('onboarding.profile.birthdayRequired')); return; }
    if (getAge(birthday) < 13) { setError(t('onboarding.profile.mustBe13')); return; }

    setSaving(true);
    const token = getAuthToken();
    if (!token) { setSaving(false); return; }

    try {
      // Upload avatar if provided
      if (avatarFile) {
        const formData = new FormData();
        formData.append('file', avatarFile);
        const uploadRes = await fetch(`${API_URL}/scoops/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          if (uploadData.url) {
            // Update avatar
            await fetch(`${API_URL}/user/profile`, {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ avatar_url: uploadData.url }),
            });
          }
        }
      }

      // Submit profile setup
      const res = await fetch(`${API_URL}/profile/setup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: displayName.trim(),
          username: username.trim().toLowerCase(),
          birthday,
          gender: gender || undefined,
          location: location.trim() || undefined,
        }),
      });

      if (res.ok) {
        onNext();
      } else {
        const data = await res.json();
        setError(data.error || t('onboarding.profile.setupFailed'));
      }
    } catch {
      setError(t('onboarding.profile.setupFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Max date = 13 years ago
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 13);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-white mb-2">{t('onboarding.profile.title')}</h3>
        <p className="text-sm text-[#9a9aac]">{t('onboarding.profile.subtitle')}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Avatar */}
      <div className="flex justify-center mb-6">
        <label className="relative cursor-pointer group">
          <input type="file" accept="image/*" className="hidden" onChange={handleAvatarSelect} />
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-iris-500/50" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-white/4 border-2 border-dashed border-white/20 flex items-center justify-center group-hover:border-iris-500/50 transition-colors">
              <Camera size={28} className="text-[#9a9aac] group-hover:text-iris-400 transition-colors" />
            </div>
          )}
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-iris-600 flex items-center justify-center shadow-lg">
            <Camera size={14} className="text-white" />
          </div>
        </label>
      </div>

      <div className="space-y-4">
        {/* Display Name */}
        <div>
          <label htmlFor="onb-display-name" className="block text-sm font-medium text-[#a0a0b0] mb-1.5">
            {t('onboarding.profile.displayName')} <span className="text-red-400">*</span>
          </label>
          <input
            id="onb-display-name"
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder={t('onboarding.profile.displayNamePlaceholder')}
            className="w-full px-4 py-3 bg-white/4 border border-white/10 rounded-lg text-white placeholder-[#9a9aac] focus:outline-hidden focus:border-iris-500/50 transition-colors"
            maxLength={50}
            autoFocus
          />
        </div>

        {/* Username */}
        <div>
          <label htmlFor="onb-username" className="block text-sm font-medium text-[#a0a0b0] mb-1.5">
            {t('onboarding.profile.username')} <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9a9aac]">@</span>
            <input
            id="onb-username"
              type="text"
              value={username}
              onChange={e => { setUserEditedUsername(true); setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')); }}
              placeholder={t('profile.usernamePlaceholder')}
              className="w-full pl-9 pr-10 py-3 bg-white/4 border border-white/10 rounded-lg text-white placeholder-[#9a9aac] focus:outline-hidden focus:border-iris-500/50 transition-colors"
              maxLength={30}
            />
            {usernameStatus.checking && (
              <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9a9aac] animate-spin" />
            )}
            {!usernameStatus.checking && usernameStatus.available === true && (
              <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400" />
            )}
            {!usernameStatus.checking && usernameStatus.available === false && (
              <X size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400" />
            )}
          </div>
          {usernameStatus.available === false && (
            <p className="mt-1 text-xs text-red-400">{t('onboarding.profile.usernameTaken')}</p>
          )}
        </div>

        {/* Birthday */}
        <div>
          <label htmlFor="onb-birthdate" className="block text-sm font-medium text-[#a0a0b0] mb-1.5">
            {t('onboarding.profile.birthday')} <span className="text-red-400">*</span>
          </label>
          <input
            id="onb-birthdate"
            type="date"
            value={birthday}
            onChange={e => setBirthday(e.target.value)}
            max={maxDateStr}
            className="w-full px-4 py-3 bg-white/4 border border-white/10 rounded-lg text-white focus:outline-hidden focus:border-iris-500/50 transition-colors scheme-dark"
          />
          <p className="mt-1 text-xs text-[#9a9aac]">{t('onboarding.profile.birthdayHint')}</p>
        </div>

        {/* Gender (optional) */}
        <div>
          <label className="block text-sm font-medium text-[#a0a0b0] mb-2">
            {t('onboarding.profile.gender', 'Gender')}
          </label>
          <div className="flex gap-3">
            {[
              { value: 'male', label: t('onboarding.profile.genderMale', 'Male') },
              { value: 'female', label: t('onboarding.profile.genderFemale', 'Female') },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setGender(gender === opt.value ? '' : opt.value)}
                className={`flex-1 py-3 rounded-lg text-sm font-medium transition-all ${
                  gender === opt.value
                    ? 'bg-iris-500/20 text-iris-300 border border-iris-500/40'
                    : 'bg-white/4 text-[#a0a0b0] border border-white/10 hover:border-white/20 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Location (optional) */}
        <div>
          <label htmlFor="onb-location" className="block text-sm font-medium text-[#a0a0b0] mb-1.5">
            {t('onboarding.profile.location')}
          </label>
          <input
            id="onb-location"
            type="text"
            value={location}
            onChange={e => setLocation(e.target.value)}
            placeholder={t('onboarding.profile.locationPlaceholder')}
            className="w-full px-4 py-3 bg-white/4 border border-white/10 rounded-lg text-white placeholder-[#9a9aac] focus:outline-hidden focus:border-iris-500/50 transition-colors"
            maxLength={100}
          />
        </div>

        {/* Phone (optional with inline verification) */}
        <div>
          <label htmlFor="onb-phone" className="block text-sm font-medium text-[#a0a0b0] mb-1.5 flex items-center gap-1.5">
            <Smartphone size={14} />
            {t('onboarding.profile.phone', 'Phone')}
            <span className="text-[#9a9aac] font-normal text-xs">({t('onboarding.profile.optional', 'optional')})</span>
          </label>
          {phoneStep === 'verified' ? (
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
              <Check size={16} className="text-emerald-400" />
              <span className="text-sm text-emerald-300">{phone.replace(/[\s\-()]/g, '')}</span>
              <span className="text-xs text-emerald-400/70 ml-1">{t('onboarding.profile.phoneVerified', 'Verified')}</span>
              <div className="ml-auto flex items-center gap-1 text-[10px] text-emerald-400/60">
                <Shield size={10} /> +15 {t('onboarding.profile.trustPoints', 'trust')}
              </div>
            </div>
          ) : phoneStep === 'code' ? (
            <div className="space-y-2">
              <p className="text-xs text-[#a0a0b0]">{t('onboarding.profile.enterCode', 'Enter the 6-digit code sent to your phone')}</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={phoneCode}
                  onChange={e => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-32 px-3 py-2.5 bg-white/4 border border-white/10 rounded-lg text-white text-center font-mono tracking-[0.3em] focus:outline-hidden focus:border-iris-500/50"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleVerifyPhoneCode}
                  disabled={phoneVerifying || phoneCode.length !== 6}
                  className="px-4 py-2.5 text-sm font-medium text-[#0C0C12] bg-iris-600 rounded-lg hover:bg-iris-500 disabled:opacity-40 flex items-center gap-1.5"
                >
                  {phoneVerifying && <Loader2 size={14} className="animate-spin" />}
                  {t('onboarding.profile.verify', 'Verify')}
                </button>
              </div>
              <div className="flex items-center gap-3">
                {phoneCountdown > 0 ? (
                  <span className="text-[11px] text-[#9a9aac]">{t('onboarding.profile.resendIn', { seconds: phoneCountdown, defaultValue: 'Resend in {seconds}s' })}</span>
                ) : (
                  <button type="button" onClick={handleSendPhoneCode} className="text-[11px] text-iris-400 hover:text-iris-300">
                    {t('onboarding.profile.resendCode', 'Resend Code')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                id="onb-phone"
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder={t('onboarding.profile.phonePlaceholder', '+1 (555) 123-4567')}
                className="flex-1 px-4 py-3 bg-white/4 border border-white/10 rounded-lg text-white placeholder-[#9a9aac] focus:outline-hidden focus:border-iris-500/50 transition-colors"
              />
              {phone.trim().length > 3 && (
                <button
                  type="button"
                  onClick={handleSendPhoneCode}
                  disabled={phoneStep === 'sending'}
                  className="px-4 py-3 text-sm font-medium text-iris-300 bg-iris-500/10 border border-iris-500/30 rounded-lg hover:bg-iris-500/20 disabled:opacity-40 flex items-center gap-1.5 whitespace-nowrap"
                >
                  {phoneStep === 'sending' && <Loader2 size={14} className="animate-spin" />}
                  {t('onboarding.profile.verifyBtn', 'Verify')}
                </button>
              )}
            </div>
          )}
          {phoneError && (
            <p className="mt-1 text-xs text-red-400 flex items-center gap-1"><AlertCircle size={12} />{phoneError}</p>
          )}
          {phoneStep !== 'verified' && !phoneError && (
            <p className="mt-1 text-xs text-[#9a9aac]">
              {t('onboarding.profile.phoneHint', 'Verifying your phone boosts your trust score by +15 points')}
            </p>
          )}
        </div>
      </div>

      {/* Continue Button */}
      <button
        onClick={handleSubmit}
        disabled={saving || !displayName.trim() || !username || !birthday}
        className="mt-6 w-full py-3 rounded-xl text-sm font-medium text-[#0C0C12] bg-iris-600 hover:bg-iris-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
      >
        {saving ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            {t('onboarding.profile.saving')}
          </>
        ) : (
          t('onboarding.profile.continue')
        )}
      </button>
    </div>
  );
}


// ─── Language Picker Step ────────────────────────────────────────────────────
// Inline so it shares the modal chrome and progress bar. Mirrors the
// /welcome page in apps/web-vite/src/pages/Welcome.tsx — keep them in
// sync if the language list grows.

const SUPPORTED_LANGUAGES_INLINE: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'he', label: 'עברית' },
  { code: 'ar', label: 'العربية' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'pl', label: 'Polski' },
  { code: 'nl', label: 'Nederlands' },
];

function OnboardingLanguageStep({ onNext }: { onNext: (codes: string[]) => void }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(() => {
    const browserLang = (typeof navigator !== 'undefined' ? navigator.language : 'en').slice(0, 2).toLowerCase();
    const set = new Set<string>(['en']);
    if (browserLang && browserLang !== 'en') set.add(browserLang);
    return set;
  });

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (code === 'en') return prev; // English locked — universal base
      if (next.has(code)) next.delete(code);
      else next.add(code);
      next.add('en');
      return next;
    });
  }

  return (
    <div className="p-6 sm:p-8">
      <h2 className="text-xl font-semibold text-white mb-2">
        {t('onboarding.language.title', 'Pick your languages')}
      </h2>
      <p className="text-sm text-[#a0a0b0] mb-6 leading-relaxed">
        {t('onboarding.language.subtitle', 'Soltik shows English to everyone. Add other languages you read so you see content in them too. You can change this anytime in Settings.')}
      </p>

      <div className="grid grid-cols-3 gap-2 mb-6">
        {SUPPORTED_LANGUAGES_INLINE.map((lang) => {
          const isSelected = selected.has(lang.code);
          const isLocked = lang.code === 'en';
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => toggle(lang.code)}
              disabled={isLocked}
              data-testid={`onb-lang-${lang.code}`}
              className={`relative px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                isSelected
                  ? 'bg-iris-500/15 border-iris-500/40 text-white'
                  : 'bg-white/2 border-white/8 text-[#a0a0b0] hover:border-white/20'
              } ${isLocked ? 'cursor-default opacity-90' : ''}`}
            >
              {lang.label}
              {isSelected && <Check size={12} className="absolute top-1.5 right-1.5 text-iris-400" />}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => onNext(Array.from(selected))}
        data-testid="onb-lang-continue"
        className="w-full px-4 py-3 rounded-xl bg-iris-600 hover:bg-iris-500 text-[#0C0C12] font-medium transition-colors"
      >
        {t('onboarding.language.continue', 'Continue')}
      </button>
    </div>
  );
}
